/**
 * Elevation sorting utilities for Native ISM.
 *
 * Normalizes and sorts elevations by display order first, then title as a stable tie-breaker.
 *
 * @module nativeInteractiveSiteMap/services/elevationSortService
 */

import { DEFAULT_ELEVATION_LABEL } from '../constants';

function normalizeText(value) {
  return String(value || '').trim();
}

/**
 * Value sent as the Lead API's Elevation parameter (NHS20-9080): the image title when it has one,
 * otherwise the default label plus its Display Order (e.g. "Elevation 2") so the builder can
 * still tell which elevation was picked — the on-screen "Elevation" fallback label alone doesn't
 * identify it. Falls back to the 1-based position when Display Order is missing too.
 *
 * @param {Object} elevation - Raw elevation image ({ title, displayOrder }).
 * @param {number} index - The elevation's position in its source list.
 * @returns {string}
 */
export function getElevationLeadValue(elevation, index = 0) {
  const title = normalizeText(elevation?.title);
  if (title) {
    return title;
  }
  return `${DEFAULT_ELEVATION_LABEL} ${elevation?.displayOrder ?? index + 1}`;
}

/**
 * @param {Array} elevations
 * @returns {Array<{
 *   id: string, title: string, leadValue: string, description: string, url: string|null,
 *   displayOrder: number|undefined
 * }>}
 */
export default function sortElevations(elevations = []) {
  const elevationList = Array.isArray(elevations) ? elevations : [];

  return elevationList
    .map((elevation, index) => ({
      id: elevation.id || String(index),
      // Left blank when the image has no title — the selector shows no label for it (NHS20-9080).
      title: normalizeText(elevation?.title),
      leadValue: getElevationLeadValue(elevation, index),
      description: normalizeText(elevation?.description),
      url: elevation?.url || null,
      displayOrder: elevation?.displayOrder,
    }))
    .sort((a, b) => {
      const aOrder = Number.isFinite(a.displayOrder) ? a.displayOrder : Number.MAX_SAFE_INTEGER;
      const bOrder = Number.isFinite(b.displayOrder) ? b.displayOrder : Number.MAX_SAFE_INTEGER;

      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    });
}